import React, { useEffect } from "react";

export type KeyMap = { [key: string]: string[] };
export type HotKeyHandlers<T> = { [key: string]: (event?: T) => void };

export type HotKeyProps = Props<React.KeyboardEvent<HTMLElement>>;
export type Handlers = HotKeyHandlers<React.KeyboardEvent<HTMLElement>>;
export type GlobalHotKeyProps = Props<KeyboardEvent>;
export type GlobalHandlers = HotKeyHandlers<KeyboardEvent>;

interface Props<T> {
  handlers: HotKeyHandlers<T>;
  keyMap: KeyMap;
  children: React.ReactNode;
}

/**
  HotKeys component is used to handle keyboard events on children of this component.
  Usage: `<HotKeys handlers={{ onEnter: () => console.log('Enter') }} keyMap={{ onEnter: [keyCodeTypes.Enter] }}>
    <div>Press Enter</div>
  </HotKeys>`
 */
export const HotKeys = ({ handlers, keyMap, children }: HotKeyProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    Object.keys(keyMap).map(cb => {
      keyMap[cb].map(key => {
        if (key === event.code && !event.shiftKey) {
          handlers[cb]?.(event);
        }
      });
    });
  };

  return <span onKeyDown={handleKeyDown}>
    {children}
  </span>;
};

/**
  GlobalHotKeys component is used to handle keyboard events on any DOM element outside the GlobalHotkeys component.
  Usage:
  `<div>Press Enter</div>
  <GlobalHotKeys handlers={{ onEnter: () => console.log('Enter') }} keyMap={{ onEnter: [keyCodeTypes.Enter] }}>
    <div>Child</div>
  </GlobalHotKeys>`
 */
export const GlobalHotKeys = ({ handlers, keyMap, children }: GlobalHotKeyProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      Object.keys(keyMap).map(callback => {
        keyMap[callback].map(keyCodeType => {
          if (keyCodeType === event.code && !event.shiftKey) {
            handlers[callback]?.(event);
          }
        });
      });
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return <>
    {children}
  </>;
};
